/**
 * Reviewing what the reference list is built from.
 *
 * The list is derived, not stored: every change to the deck recomputes it. That
 * makes it correct but opaque — an author looking at a reference slide cannot
 * tell which slide put an entry there, whether a source they added was picked
 * up, or why an item they typed by hand appears only once.
 *
 * This builds the answer: every citation the deck contributes, tagged with
 * where it came from, alongside the items typed into the reference block, with
 * the overlap between them marked so the duplicates can be cleared out.
 */

import {
  collectSourceEntriesFromSections,
  formatReference,
  mapCitationToSourceMeta
} from '../lib/apaSlideFormatter.js';
import { hasApaCitationContent } from './apaCitationUtils';

const BLOCK_TYPE_LABELS = {
  image: 'Image',
  gallery: 'Gallery',
  video: 'Video',
  'apa-quote': 'Quote',
  'apa-citation': 'Citation block',
  html: 'Embedded HTML',
  'multi-column-cards': 'Columns'
};

/** How a source is described in the review list: "Bridge-in · Image". */
export const describeOrigin = (origin = {}) => {
  const kind = origin.kind === 'detached'
    ? 'Torn-off note'
    : (BLOCK_TYPE_LABELS[origin.blockType] || 'Slide content');
  const section = origin.sectionTitle;
  return section ? `${section} · ${kind}` : kind;
};

/**
 * The reference a source renders to, or '' when it renders to nothing.
 *
 * `formatReference` returns null on purpose — an image with no artist of its
 * own contributes no reference entry, and neither does a half-filled citation
 * with nothing referenceable in it. Every reader here has to cope with that:
 * calling `.trim()` on the null took the whole editor down with it the moment
 * a deck containing one such source opened the reference review.
 */
const referenceTextOf = (meta) => {
  const text = formatReference(meta);
  return typeof text === 'string' ? text : '';
};

/**
 * Two entries are the same source when they render to the same reference. That
 * is the same test dedupeSourcesByReference applies when building the list, so
 * "duplicate" here means exactly "the list would have shown these as one".
 *
 * A source with no reference gets an empty key, and callers drop it — it is not
 * in the list, so it cannot be a duplicate of anything in it.
 */
const referenceKey = (meta) => referenceTextOf(meta).trim().toLowerCase();

const manualCitationsOf = (block = {}) => (
  Array.isArray(block.citations)
    ? block.citations
    : Array.isArray(block.references)
      ? block.references
      : []
);

/**
 * @param sections the deck
 * @param manualCitations the reference block's own items — passed separately so
 *   the panel can review unsaved edits sitting in the modal, not just what was
 *   last saved onto the block.
 */
export const buildReferenceReview = ({ sections = [], manualCitations = [] } = {}) => {
  const slideEntries = collectSourceEntriesFromSections(sections);

  // Collapse the deck's citations the way the list does, keeping every origin
  // so a source used on three slides is one entry that says so. Sources whose
  // citation is switched off are held back for the "not in the list" group.
  const bySource = new Map();
  const excludedBySource = new Map();
  slideEntries.forEach(({ meta, origin }) => {
    const key = referenceKey(meta);
    if (!key) return;
    const bucket = origin.included === false ? excludedBySource : bySource;
    const existing = bucket.get(key);
    if (existing) {
      existing.origins.push(origin);
      return;
    }
    bucket.set(key, { key, reference: referenceTextOf(meta), meta, origins: [origin] });
  });
  const slideCitations = [...bySource.values()];
  // A source switched off on one slide but still live on another is in the
  // list, so it does not belong in the excluded group.
  const excluded = [...excludedBySource.values()].filter((entry) => !bySource.has(entry.key));

  const slideKeys = new Set(slideCitations.map((entry) => entry.key));
  const manual = manualCitations.map((citation, index) => {
    const meta = mapCitationToSourceMeta(citation);
    const key = referenceKey(meta);
    // A half-started item still formats to something ("(n.d.)."), so emptiness
    // is judged on the fields the author filled in, not the rendered string.
    const isEmpty = !hasApaCitationContent(citation);
    return {
      index,
      citation,
      reference: referenceTextOf(meta),
      key,
      // A hand-typed item is never merged into an auto-collected one:
      // dedupeSourcesByReference deliberately keeps everything the author typed,
      // so an author who enters two similar references gets both. That means a
      // hand-typed copy of a source a slide already contributes is not merely
      // redundant — the reference list is printing that source twice.
      isDuplicate: !isEmpty && Boolean(key) && slideKeys.has(key),
      isEmpty
    };
  });

  const manualKept = manual.filter((item) => !item.isEmpty);

  // One list in the order the slides print it, so the panel can be read against
  // the reference slide line by line.
  const entries = [
    ...slideCitations.map((entry) => ({
      kind: 'slide',
      key: entry.key,
      reference: entry.reference,
      origins: entry.origins,
      // Only a source with an include flag can be dropped from the panel;
      // a quote or a citation block has to be edited on its own slide.
      canExclude: entry.origins.some((origin) => Boolean(origin.includeField))
    })),
    ...manualKept.map((item) => ({
      kind: 'manual',
      key: item.key,
      reference: item.reference,
      index: item.index,
      isDuplicate: item.isDuplicate,
      canExclude: true
    }))
  ].sort((a, b) => (a.reference || '').localeCompare(b.reference || ''));

  return {
    entries,
    slideCitations,
    excluded,
    manual,
    duplicateIndexes: manual.filter((item) => item.isDuplicate).map((item) => item.index),
    // What the reference slides actually print: the deck's sources collapsed to
    // one entry each, plus every item the author typed, kept as typed.
    totalReferences: slideCitations.length + manualKept.length
  };
};

/** Compare two reviews so a rescan can say what actually changed. */
export const diffReferenceReview = (before, after) => {
  const beforeKeys = new Set((before?.slideCitations || []).map((entry) => entry.key));
  const afterKeys = new Set((after?.slideCitations || []).map((entry) => entry.key));
  return {
    added: (after?.slideCitations || []).filter((entry) => !beforeKeys.has(entry.key)),
    removed: (before?.slideCitations || []).filter((entry) => !afterKeys.has(entry.key))
  };
};

export const referenceReviewFor = (sections, block) =>
  buildReferenceReview({ sections, manualCitations: manualCitationsOf(block) });

/**
 * Switch a collected source's citation on or off, wherever it lives.
 *
 * The reference list has no store of its own to edit — an entry is in the list
 * because some block put it there — so removing one means reaching back to that
 * block and clearing its include flag. That is the same flag the block's own
 * "Add to reference list" checkbox sets, and the same one the tear-off uses, so
 * the note under the media follows the list. Nothing is deleted: the citation
 * details stay on the block and switching it back on restores the entry.
 */
export const setSourceInclusion = (sections = [], origin = {}, included = true) => {
  const { blockId, includeField } = origin;
  if (!blockId || !includeField) return sections;

  const applyToItem = (item) => (item ? { ...item, [includeField]: included } : item);

  return (sections || []).map((section) => {
    if (!(section?.blocks || []).some((block) => block?.id === blockId)) return section;
    return {
      ...section,
      blocks: (section.blocks || []).map((block) => {
        if (block?.id !== blockId) return block;

        if (typeof origin.itemIndex === 'number') {
          return {
            ...block,
            items: (block.items || []).map((item, index) => (
              index === origin.itemIndex ? applyToItem(item) : item
            ))
          };
        }

        if (typeof origin.columnIndex === 'number' && typeof origin.cardIndex === 'number') {
          return {
            ...block,
            columns: (block.columns || []).map((column, columnIndex) => {
              if (columnIndex !== origin.columnIndex) return column;
              return {
                ...column,
                cards: (column.cards || []).map((card, cardIndex) => {
                  if (cardIndex !== origin.cardIndex) return card;
                  const slot = includeField === 'videoIncludeCitation' ? 'video' : 'image';
                  return { ...card, [slot]: applyToItem(card[slot]) };
                })
              };
            })
          };
        }

        return { ...block, [includeField]: included };
      })
    };
  });
};
